import _ from 'lodash'
import { useMemo } from 'react'
import { CellStyleFunc, ValueFormatterParams } from 'ag-grid-community'

import {
  CustomEnumValue,
  FunctionProperty,
  PropertyType,
} from '../../../lib/commons/appFunction'
import { getLabel } from '../../../lib/commons/i18nLabel'
import { CustomEnumCellEditor } from '../../containers/BulkSheetView/components/cellEditor'
import {
  CustomEnumCellRenderer,
  DefaultCellRenderer,
} from '../../containers/BulkSheetView/components/cellRenderer'
import {
  ClientSideTextFilter,
  CustomEnumFilter,
} from '../../containers/BulkSheetView/components/filter'
import { ColumnType } from '../../containers/commons/AgGrid'
import { BulkSheetProperty } from './bulksheetProperty'
import { intl } from '../../../i18n'
import { TRUE } from '../../../utils/boolExpression'

// The following methods should be used in src/view/page-properties/bulksheet-properties

export const floatValueFormatter = (
  params: ValueFormatterParams,
  nanString: string,
  digits: number
) => {
  const value: number | undefined = parseFloat(params.value)
  if (!isFinite(value)) return nanString
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

export const useCustomEnumColumn = ({
  field,
  customEnumCode,
  customEnumOptions,
}: {
  field: string
  customEnumCode: string
  customEnumOptions?: CustomEnumValue[] // TODO: GridOption.context cannot be obtained from ColDef.comprator, so pass it as an argument.
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      width: 90,
      editable: true,
      type: [ColumnType.customEnum],
      cellEditor: CustomEnumCellEditor,
      cellEditorParams: { customEnumCode },
      cellRenderer: CustomEnumCellRenderer,
      cellRendererParams: { customEnumCode },
      filter: CustomEnumFilter,
      floatingFilter: true,
      filterParams: {
        customEnumCode,
        getValue: (option: CustomEnumValue) => option?.value,
        getLabel: (option: CustomEnumValue) =>
          option ? (getLabel(option.nameI18n) ?? option.name) : '',
        sortValues: (options, context) => {
          const customEnums = context[customEnumCode]
          if (!customEnums) return options
          return options.sort((a, b) => {
            return (
              customEnums.findIndex(e => e.value === a?.value) -
              customEnums.findIndex(e => e.value === b?.value)
            )
          })
        },
      },
      comparator: (valueA: string, valueB: string) => {
        if (!customEnumOptions || _.isEmpty(customEnumOptions)) return 0
        return (
          customEnumOptions.findIndex(o => o.value === valueA) -
          customEnumOptions.findIndex(o => o.value === valueB)
        )
      },
    }),
    [field, customEnumCode, customEnumOptions]
  )
  return property
}

export const useMultiLineTextColum = (field: string) => {
  const property = useMemo(
    () => ({
      field,
      editable: true,
      type: [ColumnType.multiLineText],
      floatingFilter: true,
      filter: ClientSideTextFilter,
    }),
    [field]
  )
  return property
}

export const useCode = (
  field: string,
  override?: {
    cellStyle?: CellStyleFunc
  }
): BulkSheetProperty => {
  const property = useMemo(() => {
    const uiMeta = {
      propertyType: PropertyType.Text,
      charactersAllowed: 'a-zA-Z0-9_-',
      maxLength: 32,
      requiredIf: TRUE,
    } as Partial<FunctionProperty>

    const property: BulkSheetProperty = {
      field,
      headerName: intl.formatMessage({ id: 'code' }),
      hide: false,
      pinned: false,
      width: 100,
      editable: params => params.data.added,
      cellRenderer: DefaultCellRenderer,
      cellRendererParams: {
        uiMeta,
      },
      cellEditorParams: {
        uiMeta,
      },
      filter: ClientSideTextFilter,
      floatingFilter: true,
    }
    if (override) {
      if (override.cellStyle) {
        property.cellStyle = override.cellStyle
      }
    }
    return property
  }, [field])
  return property
}
